'use client';

import { ShoppingBag, AlertTriangle } from 'lucide-react';
import React from 'react';

interface ScoreBreakdown {
  purchaseBehavior: {
    totalOrders: number;
  };
  paymentHistory: {
    latePayments: number;
  };
}

interface ScoreFactorsProps {
  breakdown: ScoreBreakdown | null;
}

/**
 * Displays key factors affecting the reputation score.
 */
function ScoreFactors({ breakdown }: ScoreFactorsProps) {
  const purchases = breakdown?.purchaseBehavior?.totalOrders ?? 0;
  const latePayments = breakdown?.paymentHistory?.latePayments ?? 0;

  const factors = [
    {
      icon: ShoppingBag,
      label: 'Purchases',
      value: `${purchases}/3`,
      status: purchases >= 3 ? 'good' : 'progress',
      description: purchases >= 3 ? 'Great work!' : 'Buy more to improve',
    },
    {
      icon: AlertTriangle,
      label: 'Late payments',
      value: latePayments.toString(),
      status: latePayments === 0 ? 'good' : 'warning',
      description: latePayments === 0 ? 'In good standing' : 'Pay on time to improve',
    },
  ];

  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Reputation Score key factors</h3>
      <div className="grid grid-cols-2 gap-3">
        {factors.map((factor) => (
          <div
            key={factor.label}
            className="rounded-lg border bg-white p-4 text-center"
          >
            <factor.icon
              className={`mx-auto mb-2 size-6 ${
                factor.status === 'good'
                  ? 'text-green-500'
                  : factor.status === 'warning'
                  ? 'text-orange-500'
                  : 'text-blue-500'
              }`}
            />
            <p className="text-xs text-gray-500">{factor.label}</p>
            <p className="text-2xl font-bold">{factor.value}</p>
            <p className="text-xs text-gray-600">{factor.description}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

export default ScoreFactors;
